/**
 * PathGrid — renders a grid with a path of movements.
 *
 * data: {
 *   start: [0, 0],               // [col, row] starting position
 *   moves: ["N", "N", "N", "E", "E"],  // sequence of moves
 *   cols: 6,                      // grid width (optional, auto-calculated)
 *   rows: 6,                      // grid height (optional, auto-calculated)
 *   showEnd: true,                // show "?" at final position (default true)
 * }
 *
 * Directions: N (up), S (down), E (right), O/W (left)
 */

const DIR_DELTA = {
    'N': [0, 1], 'S': [0, -1], 'E': [1, 0], 'O': [-1, 0], 'W': [-1, 0],
    'NE': [1, 1], 'NO': [-1, 1], 'NW': [-1, 1], 'SE': [1, -1], 'SO': [-1, -1], 'SW': [-1, -1],
};

export default function PathGrid({ data }) {
    if (!data?.moves || !Array.isArray(data.moves)) return null;

    const start = data.start || [0, 0];
    const moves = data.moves;
    const showPath = data.showPath === true;

    // Compute path
    const path = [{ x: start[0], y: start[1] }];
    let cx = start[0], cy = start[1];
    for (const move of moves) {
        // Handle "N 3" format (direction + count)
        const match = move.match(/^([NSEO]O?|[NS][EO]|[NEWS]W?)\s*(\d+)?$/i);
        if (!match) continue;
        const dir = match[1].toUpperCase();
        const count = parseInt(match[2] || '1', 10);
        const delta = DIR_DELTA[dir];
        if (!delta) continue;
        for (let step = 0; step < count; step++) {
            cx += delta[0];
            cy += delta[1];
            path.push({ x: cx, y: cy });
        }
    }

    // Compute grid bounds
    const allX = path.map(p => p.x);
    const allY = path.map(p => p.y);

    let minX, maxX, minY, maxY;
    if (showPath) {
        // Solution mode: fit to actual path
        minX = Math.min(...allX) - 1;
        maxX = Math.max(...allX) + 1;
        minY = Math.min(...allY) - 1;
        maxY = Math.max(...allY) + 1;
    } else {
        // Question mode: generic grid around start, don't reveal path shape
        const sx = start[0], sy = start[1];
        const gridSize = data.gridSize || 7;
        minX = sx - 1;
        maxX = sx + gridSize - 1;
        minY = sy - 1;
        maxY = sy + gridSize - 1;
    }
    const cols = data.cols || (maxX - minX + 1);
    const rows = data.rows || (maxY - minY + 1);

    const cellSize = 32;
    const w = cols * cellSize;
    const h = rows * cellSize;

    // Convert grid coords to SVG coords (y is flipped: grid y=0 at bottom)
    const toSvg = (gx, gy) => ({
        sx: (gx - minX) * cellSize + cellSize / 2,
        sy: (maxY - gy) * cellSize + cellSize / 2,
    });

    const startSvg = toSvg(path[0].x, path[0].y);
    const endSvg = toSvg(path[path.length - 1].x, path[path.length - 1].y);

    return (
        <div className="qv-pathgrid">
            <svg viewBox={`0 0 ${w} ${h}`} className="qv-pathgrid__svg" style={{ maxWidth: w }}>
                {/* Grid lines */}
                {Array.from({ length: cols + 1 }, (_, i) => (
                    <line key={`v${i}`} x1={i * cellSize} y1={0} x2={i * cellSize} y2={h}
                        className="qv-pathgrid__gridline" />
                ))}
                {Array.from({ length: rows + 1 }, (_, i) => (
                    <line key={`h${i}`} x1={0} y1={i * cellSize} x2={w} y2={i * cellSize}
                        className="qv-pathgrid__gridline" />
                ))}

                {/* Axis labels */}
                {Array.from({ length: cols }, (_, i) => (
                    <text key={`lx${i}`} x={i * cellSize + cellSize / 2} y={h - 4}
                        textAnchor="middle" className="qv-pathgrid__label">
                        {minX + i}
                    </text>
                ))}
                {Array.from({ length: rows }, (_, i) => (
                    <text key={`ly${i}`} x={4} y={i * cellSize + cellSize / 2 + 1}
                        dominantBaseline="central" className="qv-pathgrid__label">
                        {maxY - i}
                    </text>
                ))}

                {/* Path segments — only shown in solution mode */}
                {showPath && path.slice(1).map((p, i) => {
                    const prev = toSvg(path[i].x, path[i].y);
                    const curr = toSvg(p.x, p.y);
                    return (
                        <line key={`seg${i}`}
                            x1={prev.sx} y1={prev.sy} x2={curr.sx} y2={curr.sy}
                            className="qv-pathgrid__path"
                        />
                    );
                })}

                {/* Path dots — solution mode only */}
                {showPath && path.slice(1, -1).map((p, i) => {
                    const s = toSvg(p.x, p.y);
                    return <circle key={`dot${i}`} cx={s.sx} cy={s.sy} r={3} className="qv-pathgrid__dot" />;
                })}

                {/* Start marker */}
                <circle cx={startSvg.sx} cy={startSvg.sy} r={8} className="qv-pathgrid__start" />
                <text x={startSvg.sx} y={startSvg.sy} textAnchor="middle" dominantBaseline="central"
                    className="qv-pathgrid__start-label">D</text>

                {/* End marker — solution mode only */}
                {showPath && (
                    <>
                        <circle cx={endSvg.sx} cy={endSvg.sy} r={10} className="qv-pathgrid__end" />
                        <text x={endSvg.sx} y={endSvg.sy} textAnchor="middle" dominantBaseline="central"
                            className="qv-pathgrid__end-label">A</text>
                    </>
                )}
            </svg>
        </div>
    );
}
